import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AcceptedPaper.css';

const AcceptedPaper = () => {
    const [papers, setPapers] = useState([]);
    const [filteredPapers, setFilteredPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [filter, setFilter] = useState('All'); // 'All', 'Oral', 'Poster'
    const [searchQuery, setSearchQuery] = useState('');
    const papersPerPage = 10;

    useEffect(() => {
        fetchAcceptedPapers();
    }, []);

    const fetchAcceptedPapers = () => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-papers`)
            .then(response => {
                // Filter only accepted papers
                const acceptedPapers = response.data.data.filter(paper => paper.status === 'Accepted');
                setPapers(acceptedPapers);
                setFilteredPapers(acceptedPapers);
            })
            .catch(error => {
                console.error('Error fetching papers:', error);
                setError(error.message);
            })
            .finally(() => setLoading(false));
    };

    // Filter papers based on presentation type and search query
    useEffect(() => {
        let filtered = papers;
        
        // Apply presentation type filter
        if (filter !== 'All') {
            filtered = filtered.filter(paper => paper.presentationType === filter);
        }
        
        // Apply search query filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(paper => {
                const paperIdMatch = paper.paperID?.toString().includes(query);
                const titleMatch = paper.title?.toLowerCase().includes(query);
                return paperIdMatch || titleMatch;
            });
        }
        
        setFilteredPapers(filtered);
        setCurrentPage(1); // Reset to first page when filter changes
    }, [filter, papers, searchQuery]);

    const handleFilterChange = (filterType) => {
        setFilter(filterType);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const clearSearch = () => {
        setSearchQuery('');
    };

    // Pagination logic
    const indexOfLastPaper = currentPage * papersPerPage;
    const indexOfFirstPaper = indexOfLastPaper - papersPerPage;
    const currentPapers = filteredPapers.slice(indexOfFirstPaper, indexOfLastPaper);
    const totalPages = Math.ceil(filteredPapers.length / papersPerPage);

    const handlePrev = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    if (loading) {
        return (
            <div className="accepted-container">
                <div className="loading-state" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: '1rem'
                }}>
                    <div className="loading-spinner" style={{
                        width: '48px',
                        height: '48px',
                        border: '4px solid #e5e7eb',
                        borderTopColor: '#5e71f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p>Loading accepted papers...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="accepted-container">
                <div className="error-state" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: '1rem',
                    padding: '2rem'
                }}>
                    <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>Error: {error}</p>
                    <button 
                        onClick={fetchAcceptedPapers}
                        style={{
                            padding: '0.75rem 2rem',
                            background: '#5e71f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="accepted-container">
            <div className="accepted-header">
                <h1 className="accepted-title">Accepted Abstracts</h1>
                <p className="accepted-subtitle">Approved Research Works</p>
            </div>

            {/* Search Bar */}
            <div className="search-container" style={{
                maxWidth: '600px',
                margin: '0 auto 2rem',
                position: 'relative'
            }}>
                <div style={{ position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Search by Paper ID or Title..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="search-input"
                        style={{
                            width: '100%',
                            padding: '0.875rem 3rem 0.875rem 3rem',
                            fontSize: '1rem',
                            border: '2px solid #e2e8f0',
                            borderRadius: '12px',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            background: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#5e71f6';
                            e.target.style.boxShadow = '0 4px 12px rgba(94, 113, 246, 0.15)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                        }}
                    />
                    <svg 
                        style={{
                            position: 'absolute',
                            left: '1rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '20px',
                            height: '20px',
                            color: '#94a3b8',
                            pointerEvents: 'none'
                        }}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {searchQuery && (
                        <button
                            onClick={clearSearch}
                            style={{
                                position: 'absolute',
                                right: '0.75rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: '#f1f5f9',
                                border: 'none',
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                color: '#64748b'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#e2e8f0';
                                e.target.style.color = '#1e293b';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f1f5f9';
                                e.target.style.color = '#64748b';
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                {searchQuery && (
                    <p style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginTop: '0.5rem',
                        textAlign: 'center'
                    }}>
                        {filteredPapers.length} result{filteredPapers.length !== 1 ? 's' : ''} found
                    </p>
                )}
            </div>

            {/* Filter Buttons */}
            <div className="filter-container" style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '1rem',
                marginBottom: '2rem',
                flexWrap: 'wrap'
            }}>
                <button
                    className={`filter-btn ${filter === 'All' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('All')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        border: '2px solid',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: filter === 'All' ? 'linear-gradient(135deg, #5e71f6 0%, #4a5fd9 100%)' : 'white',
                        color: filter === 'All' ? 'white' : '#5e71f6',
                        borderColor: '#5e71f6'
                    }}
                >
                    All Papers ({papers.length})
                </button>
                <button
                    className={`filter-btn ${filter === 'Oral' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('Oral')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        border: '2px solid',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: filter === 'Oral' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'white',
                        color: filter === 'Oral' ? 'white' : '#10b981',
                        borderColor: '#10b981'
                    }}
                >
                    Oral Presentations ({papers.filter(p => p.presentationType === 'Oral').length})
                </button>
                <button
                    className={`filter-btn ${filter === 'Poster' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('Poster')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        border: '2px solid',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: filter === 'Poster' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'white',
                        color: filter === 'Poster' ? 'white' : '#f59e0b',
                        borderColor: '#f59e0b'
                    }}
                >
                    Poster Presentations ({papers.filter(p => p.presentationType === 'Poster').length})
                </button>
            </div>

            <div className="accepted-papers-container">
                {currentPapers.length > 0 ? (
                    currentPapers.map((paper) => (
                        <div className="accepted-paper-card" key={paper._id}>
                            <div className="accepted-paper-header">
                                <span className="accepted-paper-id">Paper ID: {paper.paperID}</span>
                                <span className="accepted-paper-category">Presentation: {paper.presentationType}</span>
                            </div>
                            <h3 className="accepted-paper-title">{paper.title}</h3>
                            <div className="accepted-paper-details">
                                <p>
                                    <strong>Authors:</strong>{" "}
                                    {paper.author.map((a) => `${a.firstName} ${a.lastName}`).join(", ")}
                                </p>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-papers-message" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        minHeight: '300px',
                        padding: '2rem'
                    }}>
                        <div className="no-papers-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                            {searchQuery ? '�' : '�📄'}
                        </div>
                        <h3 style={{ marginBottom: '1rem', color: '#333' }}>
                            {searchQuery ? 'No Papers Found' : 'No Accepted Abstracts Yet'}
                        </h3>
                        <p style={{ color: '#666', maxWidth: '500px', lineHeight: '1.5' }}>
                            {searchQuery ? (
                                <>
                                    No papers match your search query "<strong>{searchQuery}</strong>". 
                                    Try searching with a different Paper ID or Title.
                                </>
                            ) : (
                                'Accepted abstracts will be published here after the review process is complete. Please check back after the notification date (October 7th, 2025).'
                            )}
                        </p>
                        {searchQuery && (
                            <button
                                onClick={clearSearch}
                                style={{
                                    marginTop: '1.5rem',
                                    padding: '0.75rem 1.5rem',
                                    background: 'linear-gradient(135deg, #5e71f6 0%, #4a5fd9 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 12px rgba(94, 113, 246, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(94, 113, 246, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(94, 113, 246, 0.3)';
                                }}
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Pagination Controls - Only show if there are papers */}
            {filteredPapers.length > papersPerPage && (
                <div className="pagination-controls">
                    <button 
                        className="pagination-btn" 
                        onClick={handlePrev} 
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-info">
                        Page {currentPage} of {totalPages} 
                        {filter !== 'All' && ` (${filteredPapers.length} ${filter} papers)`}
                    </span>
                    <button 
                        className="pagination-btn" 
                        onClick={handleNext} 
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default AcceptedPaper;
